import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatValue } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import PlayerBadge from "@/components/PlayerBadge";
import PlayerHeadshot, { TeamPill, PosRank } from "@/components/PlayerHeadshot";
import DmeTag from "@/components/DmeTag";
import { Loader2, Trophy } from "lucide-react";

const FORMATS = [{ id: "1qb", label: "1QB" }, { id: "superflex", label: "Superflex" }, { id: "tep", label: "TE Premium" }];

export default function PublicCompare() {
  const { slugA, slugB } = useParams();
  const [fmt, setFmt] = useState("superflex");
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setD(null); setNotFound(false);
    api.get(`/public/compare/${slugA}/${slugB}`, { params: { format: fmt } })
      .then(r => setD(r.data)).catch(() => setNotFound(true));
  }, [slugA, slugB, fmt]);

  if (notFound) return (
    <PublicShell>
      <div className="card-bordered p-10 text-center">
        <p className="text-[var(--text-2)] mb-4">We couldn't build that comparison.</p>
        <Link to="/players" className="btn-primary inline-flex">Browse all players</Link>
      </div>
    </PublicShell>
  );
  if (!d) return <PublicShell><div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div></PublicShell>;

  const { a, b, verdict } = d;
  const fmtLabel = FORMATS.find(f => f.id === fmt)?.label || fmt;
  const title = `${a.name} vs ${b.name} — Dynasty Trade Value (${fmtLabel}) | Dynasty Metrics`;
  const desc = `${a.name} vs ${b.name} dynasty trade value head-to-head in ${fmtLabel} leagues. ${verdict.summary}`;
  const url = `https://dynastymetrics.com/compare/${slugA}/vs/${slugB}`;
  const ld = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        "mainEntity": [{
          "@type": "Question",
          "name": `Who is the better dynasty asset, ${a.name} or ${b.name}?`,
          "acceptedAnswer": { "@type": "Answer", "text": verdict.summary },
        }],
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Player Values", "item": "https://dynastymetrics.com/players" },
          { "@type": "ListItem", "position": 2, "name": `${a.name} vs ${b.name}`, "item": url },
        ],
      },
    ],
  };

  return (
    <PublicShell>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={`${a.name} vs ${b.name} — Dynasty Trade Value`} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>

      <div className="space-y-6 fade-up" data-testid="public-compare">
        <header>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// HEAD-TO-HEAD</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight" data-testid="compare-heading">{a.name} <span className="text-[var(--text-3)]">vs</span> {b.name}</h1>
          <div className="flex items-center gap-1 border border-[var(--border)] rounded-sm p-0.5 mt-3 w-fit">
            {FORMATS.map(f => (
              <button key={f.id} onClick={() => setFmt(f.id)} data-testid={`compare-fmt-${f.id}`}
                className={`text-xs px-2.5 py-1.5 rounded-sm transition-colors ${fmt === f.id ? "bg-[var(--accent)] text-black font-semibold" : "text-[var(--text-2)] hover:text-white"}`}>{f.label}</button>
            ))}
          </div>
        </header>

        <div className="card-bordered p-5 flex items-start gap-3" data-testid="compare-verdict">
          <Trophy className="w-5 h-5 text-[var(--accent)] mt-0.5 shrink-0" />
          <p className="text-sm text-[var(--text-2)] leading-relaxed">{verdict.summary}</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {[a, b].map(p => (
            <PlayerCol key={p.slug} p={p} isWinner={verdict.winner === p.slug} />
          ))}
        </div>

        {d.related?.length > 0 && (
          <div className="card-bordered p-5" data-testid="compare-related">
            <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-3">MORE {a.position} COMPARISONS</div>
            <div className="flex flex-wrap gap-2">
              {d.related.map(r => (
                <Link key={r.slug_b} to={`/compare/${r.slug_a}/vs/${r.slug_b}`}
                  className="text-xs px-3 py-1.5 rounded-sm border border-[var(--border)] text-[var(--text-2)] hover:text-white hover:border-[var(--accent)]/40 transition-colors"
                  data-testid={`compare-related-${r.slug_b}`}>{r.name_a} vs {r.name_b}</Link>
              ))}
            </div>
          </div>
        )}

        <div className="card-bordered p-6 text-center" data-testid="compare-cta">
          <div className="font-display text-xl font-bold mb-2">Run this trade in the calculator</div>
          <p className="text-sm text-[var(--text-2)] max-w-md mx-auto mb-4">See full age-adjusted fairness, verdicts and counter-offers — free, no login.</p>
          <Link to="/login?signup=1" className="btn-primary text-sm inline-block" data-testid="compare-cta-btn">Start free</Link>
        </div>
      </div>
    </PublicShell>
  );
}

function PlayerCol({ p, isWinner }) {
  return (
    <div className={`card-bordered p-5 ${isWinner ? "border-[var(--accent)]/50" : ""}`} data-testid={`compare-col-${p.slug}`}>
      <div className="flex items-start gap-3">
        <PlayerHeadshot playerId={p.player_id} name={p.name} team={p.team} size={56} thumb={false} className="border border-[var(--border)]" />
        <div className="min-w-0">
          <Link to={`/players/${p.slug}`} className="font-display text-xl font-bold hover:text-[var(--accent)] flex items-center gap-2">{p.name}<PlayerBadge badge={p.badge} size={18} /></Link>
          <div className="flex items-center gap-2 mt-1">
            <PosRank rank={p.pos_rank} delta={p.pos_rank_delta} />
            <TeamPill team={p.team} />
            <span className="text-xs text-[var(--text-3)] tracking-widest">{p.position}{p.age ? ` · ${p.age}y` : ""}</span>
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-end justify-between">
        <div>
          <div className="text-[9px] tracking-widest text-[var(--accent)] font-mono inline-flex items-center gap-1">DME VALUE <DmeTag /></div>
          <div className="font-mono text-3xl text-[var(--accent)] mt-0.5" data-testid={`compare-dme-${p.slug}`}>{formatValue(p.dme)}</div>
        </div>
        {isWinner && <span className="text-[10px] tracking-widest font-mono text-[var(--accent)] border border-[var(--accent)]/40 rounded-sm px-2 py-1">EDGE</span>}
      </div>
      {p.age_adjustment && (
        <div className="mt-3 text-xs text-[var(--text-2)]">
          <span className="font-mono" style={{ color: p.age_adjustment.pct > 0 ? "var(--accent)" : "var(--negative)" }}>{p.age_adjustment.pct > 0 ? "+" : ""}{p.age_adjustment.pct}% age curve</span> — {p.age_adjustment.reason}
        </div>
      )}
    </div>
  );
}
